/**
 * ClientSettingsContext — per-client admin-tunable knobs (Track B).
 *
 * Read-side: any component (e.g. cellStatus consumer in TimesheetDetailPage)
 * can call useThresholds() / useSettings() to get the current values
 * without prop-drilling. Write-side: the Settings page calls
 * setSettingRemote() which both PUTs to the backend and refreshes the
 * cache locally.
 *
 * Defaults mirror DEFAULTS in `hub/src/repos/app_settings.ts` so the FE can
 * render before the network round-trip completes (no flash of unstyled
 * thresholds).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useClientContext } from "./ClientContext";

export interface AppThresholds {
  red: number;
  green: number;
}

export interface AppSettings {
  thresholds: AppThresholds;
  production_ocr_model: string;
  training_cron_hour: number;
  training_cron_minute: number;
  training_min_observations: number;
  training_penalty_scale: number;
}

const CODED_DEFAULTS: AppSettings = {
  thresholds: { red: 0.4, green: 0.85 },
  production_ocr_model: "v4",
  training_cron_hour: 3,
  training_cron_minute: 0,
  training_min_observations: 10,
  training_penalty_scale: 1.0,
};

interface RemoteSetting {
  key: string;
  value: unknown;
  default_value: unknown;
  overridden: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

interface ClientSettingsContextValue {
  settings: AppSettings;
  loaded: boolean;
  /** Update one setting both remotely and in the local cache. */
  setSettingRemote: (key: SettableKey, value: number | string) => Promise<void>;
  /** Force a refresh from the server. */
  refresh: () => Promise<void>;
}

type SettableKey =
  | "ocr.threshold_red"
  | "ocr.threshold_green"
  | "ocr.production_model"
  | "training.cron_hour"
  | "training.cron_minute"
  | "training.min_observations_per_employee"
  | "training.penalty_scale";

const Ctx = createContext<ClientSettingsContextValue | null>(null);

function projectSettings(rows: RemoteSetting[]): AppSettings {
  const byKey = new Map(rows.map((r) => [r.key, r.value]));
  const num = (k: string, fallback: number): number => {
    const v = byKey.get(k);
    if (typeof v === "number" && Number.isFinite(v)) return v;
    return fallback;
  };
  const str = (k: string, fallback: string): string => {
    const v = byKey.get(k);
    if (typeof v === "string" && v.length > 0) return v;
    return fallback;
  };
  return {
    thresholds: {
      red: num("ocr.threshold_red", CODED_DEFAULTS.thresholds.red),
      green: num("ocr.threshold_green", CODED_DEFAULTS.thresholds.green),
    },
    production_ocr_model: str("ocr.production_model", CODED_DEFAULTS.production_ocr_model),
    training_cron_hour: num("training.cron_hour", CODED_DEFAULTS.training_cron_hour),
    training_cron_minute: num("training.cron_minute", CODED_DEFAULTS.training_cron_minute),
    training_min_observations: num(
      "training.min_observations_per_employee",
      CODED_DEFAULTS.training_min_observations,
    ),
    training_penalty_scale: num("training.penalty_scale", CODED_DEFAULTS.training_penalty_scale),
  };
}

export function ClientSettingsProvider({ children }: { children: ReactNode }) {
  const { selectedClientId } = useClientContext();
  const [settings, setSettings] = useState<AppSettings>(CODED_DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!selectedClientId) {
      setSettings(CODED_DEFAULTS);
      setLoaded(true);
      return;
    }
    try {
      const r = await fetch(
        `${document.baseURI}api/admin/app-settings?client_id=${encodeURIComponent(selectedClientId)}`,
        { credentials: "same-origin" },
      );
      if (!r.ok) {
        // Non-admin users get 403 here — that's expected; keep defaults.
        setSettings(CODED_DEFAULTS);
        setLoaded(true);
        return;
      }
      const data = (await r.json()) as { settings: RemoteSetting[] };
      setSettings(projectSettings(data.settings));
      setLoaded(true);
    } catch {
      setSettings(CODED_DEFAULTS);
      setLoaded(true);
    }
  }, [selectedClientId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setSettingRemote = useCallback(
    async (key: SettableKey, value: number | string) => {
      if (!selectedClientId) throw new Error("no client selected");
      const r = await fetch(`${document.baseURI}api/admin/app-settings`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, value, client_id: selectedClientId }),
      });
      if (!r.ok) throw new Error(`PUT app-settings: ${r.status}`);
      await refresh();
    },
    [selectedClientId, refresh],
  );

  return (
    <Ctx.Provider value={{ settings, loaded, setSettingRemote, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useClientSettings(): ClientSettingsContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useClientSettings outside ClientSettingsProvider");
  return v;
}

/** Convenience hook for the most common case — the two FE rendering thresholds. */
export function useThresholds(): AppThresholds {
  return useClientSettings().settings.thresholds;
}
